import {
  acceptCompletion,
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  Completion,
  completionKeymap,
  CompletionSource,
} from '@codemirror/autocomplete';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
  standardKeymap,
} from '@codemirror/commands';
import { html, htmlCompletionSource } from '@codemirror/lang-html';
import { esLint, javascriptLanguage } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { keywordCompletionSource, sql, SQLite } from '@codemirror/lang-sql';
import {
  bracketMatching,
  foldGutter,
  foldKeymap,
  HighlightStyle,
  indentOnInput,
  indentUnit,
  syntaxHighlighting,
} from '@codemirror/language';
import { linter, lintGutter, lintKeymap } from '@codemirror/lint';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { EditorState, Extension } from '@codemirror/state';
import {
  crosshairCursor,
  drawSelection,
  dropCursor,
  EditorView,
  highlightActiveLineGutter,
  highlightSpecialChars,
  KeyBinding,
  keymap,
  lineNumbers,
  placeholder,
  rectangularSelection,
} from '@codemirror/view';
import { tags } from '@lezer/highlight';
import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import * as eslint from 'eslint-linter-browserify/linter.min.js';

import { CodeMirrorEditorProps } from 'dashboard/components-lib/CodeEditor/CodeEditor';
import {
  javascriptKeywordCompletionSource,
  localIntellisenseCompletionSource,
} from 'dashboard/components-lib/CodeEditor/completionSources/javascript/localIntellisenseCompletionSource';
import personaSqlDatasourceCompletionSource from 'dashboard/components-lib/CodeEditor/completionSources/sql/personaSqlDatasource';
import { javascriptTemplated } from 'dashboard/components-lib/CodeEditor/languages/javascript-templated';
import { jsonTemplated } from 'dashboard/components-lib/CodeEditor/languages/json-templated';
import { sqlTemplated } from 'dashboard/components-lib/CodeEditor/languages/sql-templated';

export type DefaultExtensionsOptions = {
  completionSources?: {
    javascript?: CompletionSource[];
    sql?: CompletionSource[];
  };
  editable: boolean;
  hasError: boolean;
  language: string;
  placeholder: string | HTMLElement;
  onCommandEnter?: () => void;
  readOnly: boolean;
  schemas: CodeMirrorEditorProps['schemas'];
};

export const defaultHighlightStyle = HighlightStyle.define([
  { tag: tags.meta, color: '#404740' },
  { tag: tags.link, textDecoration: 'underline' },
  { tag: tags.heading, textDecoration: 'underline', fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.strikethrough, textDecoration: 'line-through' },
  // { tag: tags.keyword, color: '#708' },
  { tag: [tags.atom, tags.bool, tags.url, tags.contentSeparator, tags.labelName], color: '#219' },
  { tag: [tags.literal, tags.inserted], color: '#164' },
  // { tag: [tags.string, tags.deleted], color: '#a11' },
  { tag: [tags.deleted], color: '#a11' },
  // { tag: [tags.regexp, tags.escape, tags.special(tags.string)], color: '#e40' },
  // { tag: tags.definition(tags.variableName), color: '#00f' },
  { tag: tags.local(tags.variableName), color: '#30a' },
  { tag: [tags.typeName, tags.namespace], color: '#085' },
  { tag: tags.className, color: '#167' },
  { tag: [tags.special(tags.variableName), tags.macroName], color: '#256' },
  // { tag: tags.definition(tags.propertyName), color: '#00c' },
  // { tag: tags.comment, color: '#940' },
  { tag: tags.invalid, color: '#f00' },

  // Design System Overrides from defaultHighlightStyle from @lezer/highlight
  { tag: tags.definition(tags.variableName), color: Color.CoreSlate1000 },
  { tag: tags.keyword, color: Color.CorePurple600, fontWeight: '600' },
  { tag: tags.arithmeticOperator, color: Color.CoreOrange800 },
  { tag: tags.logicOperator, color: Color.CoreOrange800 },
  { tag: tags.bitwiseOperator, color: Color.CoreOrange800 },
  { tag: tags.compareOperator, color: Color.CoreOrange800 },
  { tag: tags.updateOperator, color: Color.CoreOrange800 },
  { tag: tags.definitionOperator, color: Color.CoreOrange800 },
  { tag: tags.derefOperator, color: Color.CoreOrange800 },
  { tag: tags.comment, color: Color.CoreSlate800 },
  { tag: tags.number, color: Color.CoreOrange800 },
  { tag: tags.propertyName, color: Color.CoreBlue800 },
  { tag: tags.definition(tags.propertyName), color: Color.CoreBlue800 },
  { tag: tags.string, color: Color.CoreGreen800 },
  { tag: [tags.regexp, tags.escape, tags.special(tags.string)], color: Color.CoreGreen800 },
]);

// This function creates a key binding for Command+Enter or Ctrl+Enter
function createCommandEnterKeyBinding(callback: () => void): KeyBinding {
  return {
    key: 'Mod-Enter',
    run: (_: EditorView) => {
      callback();
      return true;
    },
  };
}
const basicSetup = (): Extension[] => {
  const keymaps: KeyBinding[] = [
    ...closeBracketsKeymap,
    ...completionKeymap,
    ...defaultKeymap,
    ...foldKeymap,
    ...historyKeymap,
    ...lintKeymap,
    ...searchKeymap,
    ...standardKeymap,
    indentWithTab,
  ];

  return [
    ...[keymap.of(keymaps.flat())],
    autocompletion(),
    bracketMatching(),
    closeBrackets(),
    crosshairCursor(),
    drawSelection(),
    dropCursor(),
    foldGutter(),
    highlightActiveLineGutter(),
    highlightSelectionMatches(),
    highlightSpecialChars(),
    history(),
    indentOnInput(),
    indentUnit.of(' '.repeat(2)),
    lineNumbers(),
    rectangularSelection(),
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  ].filter(Boolean);
};

export const getDefaultExtensions = (options: DefaultExtensionsOptions): Extension[] => {
  const {
    completionSources,
    editable = true,
    hasError,
    language = 'javascript',
    placeholder: placeholderStr = '',
    onCommandEnter = () => null,
    readOnly = false,
    schemas,
  } = options;

  const defaultLightThemeOption = EditorView.theme(
    {
      '&': {
        height: 'inherit',
        width: 'inherit',
        fontSize: '12px',
        borderRadius: BorderRadius.Md,
        border: `1px solid ${Color.BorderInputDefault}`,
        backgroundColor: Color.PrimaryWhite,
        ...(hasError
          ? {
              borderColor: Color.BorderInputDanger,
            }
          : {}),
      },
      '&.cm-focused': {
        outline: 'none',
        borderColor: Color.BorderInputActive,
      },
      '.cm-gutters': {
        borderTopLeftRadius: BorderRadius.Md,
        borderBottomLeftRadius: BorderRadius.Md,
      },
      '.cm-content': {
        // Use design-system monospace font
        fontFamily: '"DM Mono", monospace',
      },
      '.cm-tooltip': {
        backgroundColor: Color.PrimaryWhite,
      },
      '.cm-completionMatchedText': {
        textDecoration: 'none',
        fontWeight: '600',
      },
      '.cm-completionInfo': {
        borderRadius: BorderRadius.Sm,
        marginLeft: Spacing[2],
        padding: Spacing[1],
        backgroundColor: Color.PrimaryWhite,
      },
      '.cm-completionInfo .cm-persona-completionInfo-type': {
        color: Color.CoreBlue800,
      },
      '.cm-activeLine, .cm-activeLineGutter': {
        // Core.Purple100 with 128/255 opacity. Necessary since the selection layer is
        // underneath (and grey)
        backgroundColor: '#F1EBFF80',
      },
      '.cm-persona-script-embed': {
        // Core.Slate100 with 128/255 opacity
        backgroundColor: '#D1F3F780',
        // Core.Slate1000 with 175/255 opacity
        color: Color.CoreTeal900,
      },
      '.cm-persona-script-embed-braces': {
        color: Color.CoreTeal900,
      },
    },
    {
      dark: false,
    },
  );

  const extensions: Extension[] = [];
  extensions.unshift(basicSetup());
  extensions.unshift(keymap.of([{ key: 'Tab', run: acceptCompletion }]));
  extensions.unshift(keymap.of([createCommandEnterKeyBinding(onCommandEnter)]));
  extensions.push(defaultLightThemeOption);

  if (placeholderStr) {
    extensions.unshift(placeholder(placeholderStr));
  }

  if (editable === false) {
    extensions.push(EditorView.editable.of(false));
  }
  if (readOnly) {
    extensions.push(EditorState.readOnly.of(true));
  }

  switch (language) {
    case 'html': {
      const htmlLanguage = html();
      extensions.push([
        htmlLanguage,
        htmlLanguage.language.data.of({ autocomplete: htmlCompletionSource }),
      ]);
      break;
    }
    case 'json':
      extensions.push(json());
      break;
    case 'jsonjs':
      extensions.push(
        jsonTemplated(
          [
            javascriptKeywordCompletionSource,
            localIntellisenseCompletionSource,
            completionSources?.javascript,
          ].flat() as CompletionSource[],
        ),
      );
      break;
    case 'javascriptTemplated':
      extensions.push(
        javascriptTemplated(
          [
            javascriptKeywordCompletionSource,
            localIntellisenseCompletionSource,
            completionSources?.javascript,
          ].flat() as CompletionSource[],
        ),
      );
      break;
    case 'sql': {
      const langDialect = SQLite;
      const sqlLanguage = sql({ dialect: langDialect });
      extensions.push([
        sqlLanguage,
        sqlLanguage.language.data.of({
          autocomplete: keywordCompletionSource(langDialect, true),
        }),
        sqlLanguage.language.data.of({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          autocomplete: personaSqlDatasourceCompletionSource(langDialect, schemas.sql),
        }),
      ]);
      break;
    }
    case 'sqljs': {
      const sqljsLanguage = sqlTemplated(
        { upperCaseKeywords: true },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        [personaSqlDatasourceCompletionSource(SQLite, schemas.sql)],
        (
          [
            javascriptKeywordCompletionSource,
            localIntellisenseCompletionSource,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            completionSources.javascript,
          ].flat() as CompletionSource[]
        ).flat(),
      );
      extensions.push(sqljsLanguage);
      break;
    }
    // javascript is default
    default: {
      extensions.push([
        javascriptLanguage,
        javascriptLanguage.data.of({
          autocomplete: javascriptKeywordCompletionSource,
        }),
        ...(completionSources?.javascript || []).map((source) => {
          return javascriptLanguage.data.of({
            autocomplete: source,
          });
        }),
      ]);
      extensions.push(lintGutter());
      extensions.push(
        linter(
          esLint(new eslint.Linter(), {
            parserOptions: {
              ecmaVersion: '2022',
              sourceType: 'script',
            },
            env: {
              node: true,
            },
          }),
        ),
      );

      break;
    }
  }

  extensions.push(
    autocompletion({
      icons: false,
      addToOptions: [
        {
          render: (completion: Completion, _state: EditorState): Node => {
            const span = document.createElement('span');
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            span.innerHTML = completion.type;
            span.style.float = 'right';
            return span;
          },
          position: 80,
        },
      ],
    }),
  );

  return [...extensions];
};
